import { datadogLogs } from '@datadog/browser-logs';
import {
  QUERY_MEAL_PLAN,
  QUERY_MEAL_PLAN_INSTRUCTIONS,
  QUERY_MEAL_PLAN_PREVIOUS_APPOINTMENT,
  QUERY_MEAL_PLAN_SUMMARY,
} from '@web-app/hooks/consts';
import { GetPreviousAppointmentResponse } from '@web-app/pages/api/meal-plan/[dailyRoomName]/get-previous-appointment';
import {
  FullDetailedMealPlanWithKeys,
  MealPlanInstructionsEntityResponse,
  MealPlanSummaryItem,
} from '@web-app/server/api/types';
import { molecule } from 'bunshi';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { v4 as uuidv4 } from 'uuid';

import {
  MacroType,
  MealPlanAnalyticsContext,
  MealPlanView,
  MealType,
} from '../../components/CreateMealPlan/utils';
import { appointmentMolecule } from '../appointmentMolecule';
import { DailyRoomNameScope } from '../scopes';
import { atomWithBroadcast } from '../utils/atomWithBroadcast';

// ============================================================================
// Exported Types
// ============================================================================

export type MacroDataKeyValue = Partial<Record<MacroType, number>>;

export interface EditMealPlanData {
  day: number;
  mealType: MealType;
  editorContent: string;
  macroData: MacroDataKeyValue;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Safely extract an error message from a failed fetch response.
 * Guards against non-JSON bodies (e.g. HTML 500 pages, load balancer timeouts).
 */
const getErrorMessage = async (res: Response): Promise<string> => {
  try {
    const body = await res.json();
    return body.error ?? res.statusText;
  } catch {
    return res.statusText;
  }
};

const getMealPlanInstructions = async (
  dailyRoomName?: string,
  healthieClientId?: number,
  healthieProviderId?: number,
): Promise<MealPlanInstructionsEntityResponse | null> => {
  if (!dailyRoomName) return null;

  const params = new URLSearchParams();
  if (healthieClientId)
    params.set('healthieClientId', String(healthieClientId));
  if (healthieProviderId)
    params.set('healthieProviderId', String(healthieProviderId));
  const qs = params.toString();

  const res = await fetch(
    `/api/meal-plan/${dailyRoomName}/get-meal-plan-instructions${qs ? `?${qs}` : ''}`,
  );

  // 404 means instructions don't exist yet for this client-provider pair
  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    datadogLogs.logger.error('Failed to fetch meal plan instructions', {
      feature: 'meal-plan',
      workflow: 'fetch-instructions',
      dailyRoomName,
      status: res.status,
      statusText: res.statusText,
    });
    throw new Error(await getErrorMessage(res));
  }

  return res.json();
};

const getMealPlanSummary = async (
  dailyRoomName?: string,
): Promise<MealPlanSummaryItem | null> => {
  if (!dailyRoomName) return null;

  const res = await fetch(
    `/api/meal-plan/${dailyRoomName}/get-meal-plan-summary`,
  );

  // 404 means summary doesn't exist yet for this client-provider pair
  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    datadogLogs.logger.error('Failed to fetch meal plan summary', {
      feature: 'meal-plan',
      workflow: 'fetch-summary',
      dailyRoomName,
      status: res.status,
      statusText: res.statusText,
    });
    throw new Error(await getErrorMessage(res));
  }

  return res.json();
};

/**
 * Fetches detailed meal plan and adds UUID keys for React rendering.
 * TODO(ISE-433): UUID generation on every fetch defeats React list reconciliation.
 * Prefer stable server-side IDs if/when the API supports them.
 */
const getDetailedMealPlan = async (
  dailyRoomName?: string,
): Promise<FullDetailedMealPlanWithKeys | null> => {
  if (!dailyRoomName) return null;

  const res = await fetch(
    `/api/meal-plan/${dailyRoomName}/get-detailed-meal-plan`,
  );

  // 404 means detailed plan doesn't exist yet for this client-provider pair
  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    datadogLogs.logger.error('Failed to fetch detailed meal plan', {
      feature: 'meal-plan',
      workflow: 'fetch-detailed-plan',
      dailyRoomName,
      status: res.status,
      statusText: res.statusText,
    });
    throw new Error(await getErrorMessage(res));
  }

  const data = (await res.json()) as FullDetailedMealPlanWithKeys;
  return {
    ...data,
    detailedMealPlan: data.detailedMealPlan.map((it) => ({
      ...it,
      key: uuidv4(),
      breakfast: { ...it.breakfast, key: uuidv4() },
      lunch: { ...it.lunch, key: uuidv4() },
      dinner: { ...it.dinner, key: uuidv4() },
      snack: { ...it.snack, key: uuidv4() },
    })),
  };
};

const getPreviousAppointment = async (
  dailyRoomName?: string,
  healthieClientId?: number,
  healthieProviderId?: number,
): Promise<GetPreviousAppointmentResponse | null> => {
  if (!dailyRoomName) return null;

  const params = new URLSearchParams();
  if (healthieClientId)
    params.set('healthieClientId', String(healthieClientId));
  if (healthieProviderId)
    params.set('healthieProviderId', String(healthieProviderId));
  const qs = params.toString();

  const res = await fetch(
    `/api/meal-plan/${dailyRoomName}/get-previous-appointment${qs ? `?${qs}` : ''}`,
  );

  // 404: no previous appointment exists for this client-provider pair
  // 400: room has no associated appointment yet
  if (res.status === 404 || res.status === 400) {
    return null;
  }

  if (!res.ok) {
    datadogLogs.logger.error('Failed to fetch previous appointment', {
      feature: 'meal-plan',
      workflow: 'fetch-previous-appointment',
      dailyRoomName,
      status: res.status,
      statusText: res.statusText,
    });
    throw new Error(await getErrorMessage(res));
  }

  return res.json();
};

// ============================================================================
// Meal Plan Molecule
// ============================================================================

/**
 * Meal Plan Molecule
 *
 * Provides all meal plan atoms scoped to a dailyRoomName (via DailyRoomNameScope,
 * inherited through appointmentMolecule). No direct scope call needed here.
 *
 * Includes:
 * - Tier 2: Query atoms for instructions, summary, and detailed meal plan
 * - Tier 3: Loading state derived atoms
 * - Tier 4: Local state atoms (optimistic updates, UI state, mutation loading)
 * - Tier 5: Derived atoms (local override falls back to query data)
 * - Tier 6: Action atoms (refetch, reset)
 */
export const mealPlanMolecule = molecule((mol, scope) => {
  const dailyRoomName = scope(DailyRoomNameScope);
  const { dailyRoomNameAtom, healthieClientIdAtom, healthieProviderIdAtom } =
    mol(appointmentMolecule);

  // ============================================================================
  // Tier 2 — QUERY ATOMS (Server state via atomWithQuery)
  // ============================================================================

  /**
   * Query atom for meal plan instructions.
   * Automatically fetches when dailyRoomName changes.
   */
  const mealPlanInstructionsQueryAtom = atomWithQuery((get) => {
    const dailyRoomName = get(dailyRoomNameAtom);
    const healthieClientId = get(healthieClientIdAtom);
    const healthieProviderId = get(healthieProviderIdAtom);
    return {
      queryKey: [
        QUERY_MEAL_PLAN_INSTRUCTIONS,
        dailyRoomName,
        healthieClientId,
        healthieProviderId,
      ],
      queryFn: async () =>
        getMealPlanInstructions(
          dailyRoomName,
          healthieClientId,
          healthieProviderId,
        ),
      enabled: !!dailyRoomName,
      retry: false,
    };
  });

  /**
   * Query atom for meal plan summary.
   * Automatically fetches when dailyRoomName changes.
   */
  const mealPlanSummaryQueryAtom = atomWithQuery((get) => {
    const dailyRoomName = get(dailyRoomNameAtom);
    return {
      queryKey: [QUERY_MEAL_PLAN_SUMMARY, dailyRoomName],
      queryFn: async () => getMealPlanSummary(dailyRoomName),
      enabled: !!dailyRoomName,
      retry: false,
    };
  });

  /**
   * Query atom for detailed meal plan.
   * Automatically fetches when dailyRoomName changes.
   */
  const detailedMealPlanQueryAtom = atomWithQuery((get) => {
    const dailyRoomName = get(dailyRoomNameAtom);
    return {
      queryKey: [QUERY_MEAL_PLAN, dailyRoomName],
      queryFn: async () => getDetailedMealPlan(dailyRoomName),
      enabled: !!dailyRoomName,
      retry: false,
    };
  });

  /**
   * Query atom for the most recent previous appointment for this client-provider pair.
   * Used to pre-populate sourceAppointmentId when generating meal plan instructions.
   */
  const previousAppointmentQueryAtom = atomWithQuery((get) => {
    const dailyRoomName = get(dailyRoomNameAtom);
    const healthieClientId = get(healthieClientIdAtom);
    const healthieProviderId = get(healthieProviderIdAtom);
    return {
      queryKey: [
        QUERY_MEAL_PLAN_PREVIOUS_APPOINTMENT,
        dailyRoomName,
        healthieClientId,
        healthieProviderId,
      ],
      queryFn: async () =>
        getPreviousAppointment(
          dailyRoomName,
          healthieClientId,
          healthieProviderId,
        ),
      enabled: !!dailyRoomName,
      retry: false,
    };
  });

  // ============================================================================
  // Tier 3 — LOADING & ERROR STATE DERIVED ATOMS
  // ============================================================================

  /**
   * Loading state for instructions query.
   *
   * Uses `isPending` (status === 'pending') rather than `isLoading`
   * (isPending && isFetching). In TanStack Query v5, a disabled query
   * (enabled: false) reports isLoading=false even though no data has been
   * fetched, which would incorrectly unblock auto-generation. isPending
   * covers both the disabled-query and the initial-fetch states.
   */
  const areMealPlanInstructionsLoadingAtom = atom((get) => {
    const queryResult = get(mealPlanInstructionsQueryAtom);
    return queryResult.isPending;
  });

  /**
   * Loading state for summary query.
   */
  const isMealPlanSummaryLoadingAtom = atom((get) => {
    const queryResult = get(mealPlanSummaryQueryAtom);
    return queryResult.isLoading;
  });

  /**
   * Loading state for detailed meal plan query.
   */
  const isDetailedMealPlanLoadingAtom = atom((get) => {
    const queryResult = get(detailedMealPlanQueryAtom);
    return queryResult.isLoading;
  });

  /**
   * Error state for instructions query.
   */
  const mealPlanInstructionsErrorAtom = atom((get) => {
    const queryResult = get(mealPlanInstructionsQueryAtom);
    return queryResult.error;
  });

  /**
   * Error state for summary query.
   */
  const mealPlanSummaryErrorAtom = atom((get) => {
    const queryResult = get(mealPlanSummaryQueryAtom);
    return queryResult.error;
  });

  /**
   * Error state for detailed meal plan query.
   */
  const detailedMealPlanErrorAtom = atom((get) => {
    const queryResult = get(detailedMealPlanQueryAtom);
    return queryResult.error;
  });

  // ============================================================================
  // Tier 4 — LOCAL STATE (optimistic updates / UI state)
  // ============================================================================

  const mealPlanInstructionsLocalAtom = atom<
    MealPlanInstructionsEntityResponse | null | undefined
  >(undefined);

  const mealPlanSummaryLocalAtom = atom<MealPlanSummaryItem | null | undefined>(
    undefined,
  );

  const detailedMealPlanLocalAtom = atom<
    FullDetailedMealPlanWithKeys | null | undefined
  >(undefined);

  /**
   * Current step in the meal plan wizard.
   * Broadcast across tabs so the MealPlanCard in the original tab
   * reflects the step the provider is on in the standalone tab.
   */
  const activeMealPlanStepAtom = atomWithBroadcast<MealPlanView>(
    `meal-plan:${dailyRoomName}:step`,
    MealPlanView.Instructions,
  );

  /**
   * Whether the initial step has been determined from loaded data.
   * Persists for the lifetime of the scope so tab remounts don't re-run
   * the initial-step logic and override the user's navigation choices.
   */
  const hasSetInitialStepAtom = atom(false);

  /**
   * Whether to include macro/nutritional data when sending meal plan.
   */
  const includeMacroDataAtom = atom(false);

  /**
   * Current meal detail being edited. Null when not editing.
   */
  const editDetailedMealPlanAtom = atom<EditMealPlanData | null>(null);

  /**
   * Whether edit mode is active for the detailed meal plan.
   */
  const detailedMealPlanEditModeAtom = atom(false);

  /**
   * True during long-running generation mutations (AI calls: generate instructions,
   * summary, or detailed plan). Used to disable the wizard's primary action button
   * and show a loading indicator across the full meal plan UI.
   * Callers MUST reset to false in a finally block to avoid stuck loading state.
   *
   * Broadcast across tabs so the MealPlanCard in the original tab
   * shows "Generating..." while the standalone tab is generating.
   */
  const isMealPlanBusyAtom = atomWithBroadcast<boolean>(
    `meal-plan:${dailyRoomName}:busy`,
    false,
  );

  /**
   * True during short edit/save mutations (server writes: saving an edited meal
   * detail row). Scoped to the detail editor so the rest of the UI stays interactive.
   * Callers MUST reset to false in a finally block to avoid stuck loading state.
   */
  const isMealPlanUpdatingAtom = atom(false);

  /**
   * Analytics context distinguishing in-session vs post-session meal plan usage.
   * Set once by CreateMealPlan on mount based on the variant prop.
   */
  const analyticsContextAtom = atom<MealPlanAnalyticsContext>('in_session');

  /**
   * Whether the standalone meal plan page (/call/meal-plan/[roomId]) is open
   * in another tab. Broadcast so the session prep tab can avoid opening the
   * drawer when the standalone page is already active.
   */
  const isStandalonePageOpenAtom = atomWithBroadcast<boolean>(
    `meal-plan:${dailyRoomName}:standalone-open`,
    false,
  );

  /**
   * Whether the meal plan has been sent to the patient.
   * Broadcast so the session prep tab card shows "sent" status
   * without waiting for a query refetch.
   */
  const isMealPlanSentAtom = atomWithBroadcast<boolean>(
    `meal-plan:${dailyRoomName}:sent`,
    false,
  );

  // ============================================================================
  // Tier 5 — DERIVED ATOMS (read from local, fallback to query)
  //
  // Three-state sentinel: undefined = no local override (fall through to query),
  // null = explicit "no data", value = local override. The write signature
  // accepts T | null (not undefined) — to release a local override and fall
  // back to query data, use refetchMealPlanAtom or resetMealPlanAtom.
  // ============================================================================

  /**
   * Derived atom: local instructions override query data.
   */
  const mealPlanInstructionsAtom = atom(
    (get) => {
      const localValue = get(mealPlanInstructionsLocalAtom);
      if (localValue !== undefined) {
        return localValue;
      }
      const queryResult = get(mealPlanInstructionsQueryAtom);
      return (queryResult.data as MealPlanInstructionsEntityResponse) ?? null;
    },
    (
      _get,
      set,
      newValue: MealPlanInstructionsEntityResponse | null | undefined,
    ) => {
      set(mealPlanInstructionsLocalAtom, newValue);
    },
  );

  /**
   * Derived atom: local summary overrides query data.
   */
  const mealPlanSummaryAtom = atom(
    (get) => {
      const localValue = get(mealPlanSummaryLocalAtom);
      if (localValue !== undefined) {
        return localValue;
      }
      const queryResult = get(mealPlanSummaryQueryAtom);
      return (queryResult.data as MealPlanSummaryItem) ?? null;
    },
    (_get, set, newValue: MealPlanSummaryItem | null) => {
      set(mealPlanSummaryLocalAtom, newValue);
    },
  );

  /**
   * Derived atom: local detailed plan overrides query data.
   */
  const detailedMealPlanAtom = atom(
    (get) => {
      const localValue = get(detailedMealPlanLocalAtom);
      if (localValue !== undefined) {
        return localValue;
      }
      const queryResult = get(detailedMealPlanQueryAtom);
      return (queryResult.data as FullDetailedMealPlanWithKeys) ?? null;
    },
    (_get, set, newValue: FullDetailedMealPlanWithKeys | null | undefined) => {
      set(detailedMealPlanLocalAtom, newValue);
    },
  );

  /**
   * Derived atom: the internal appointment UUID of the most recent previous
   * appointment for this client-provider pair. Null when no previous appointment
   * exists or the query has not resolved yet.
   */
  const previousAppointmentIdAtom = atom((get) => {
    const queryResult = get(previousAppointmentQueryAtom);
    return queryResult.data?.appointmentId ?? null;
  });

  // ============================================================================
  // Tier 6 — ACTION ATOMS (refetch, reset)
  // ============================================================================

  /**
   * Per-query refetch action atoms.
   * Use these in useCallback deps (via useSetAtom) instead of raw query objects,
   * since atomWithQuery results create new references every render.
   * Jotai's get() inside the atom always reads the latest query object.
   */
  const refetchMealPlanInstructionsAtom = atom(null, async (get) => {
    const query = get(mealPlanInstructionsQueryAtom);
    await query.refetch();
  });

  const refetchMealPlanSummaryAtom = atom(null, async (get) => {
    const query = get(mealPlanSummaryQueryAtom);
    await query.refetch();
  });

  const refetchDetailedMealPlanAtom = atom(null, async (get) => {
    const query = get(detailedMealPlanQueryAtom);
    await query.refetch();
  });

  /**
   * Action atom to refetch all meal plan queries.
   * Clears local overrides first so derived atoms surface fresh server data.
   * Uses Promise.allSettled so a single query failure doesn't abandon the others.
   */
  const refetchMealPlanAtom = atom(null, async (get, set) => {
    // Clear local overrides so Tier 5 derived atoms fall through to query data
    set(mealPlanInstructionsLocalAtom, undefined);
    set(mealPlanSummaryLocalAtom, undefined);
    set(detailedMealPlanLocalAtom, undefined);

    const instructionsQuery = get(mealPlanInstructionsQueryAtom);
    const summaryQuery = get(mealPlanSummaryQueryAtom);
    const detailedQuery = get(detailedMealPlanQueryAtom);

    await Promise.allSettled([
      instructionsQuery.refetch(),
      summaryQuery.refetch(),
      detailedQuery.refetch(),
    ]);
  });

  /**
   * Action atom to reset all local meal plan state.
   */
  const resetMealPlanAtom = atom(null, (_get, set) => {
    set(mealPlanInstructionsLocalAtom, undefined);
    set(mealPlanSummaryLocalAtom, undefined);
    set(detailedMealPlanLocalAtom, undefined);
    set(activeMealPlanStepAtom, MealPlanView.Instructions);
    set(hasSetInitialStepAtom, false);
    set(includeMacroDataAtom, false);
    set(editDetailedMealPlanAtom, null);
    set(detailedMealPlanEditModeAtom, false);
    set(isMealPlanBusyAtom, false);
    set(isMealPlanUpdatingAtom, false);
    set(analyticsContextAtom, 'in_session');
    set(isMealPlanSentAtom, false);
  });

  return {
    dailyRoomNameAtom,
    healthieClientIdAtom,
    healthieProviderIdAtom,
    mealPlanInstructionsQueryAtom,
    mealPlanSummaryQueryAtom,
    detailedMealPlanQueryAtom,
    previousAppointmentQueryAtom,
    areMealPlanInstructionsLoadingAtom,
    isMealPlanSummaryLoadingAtom,
    isDetailedMealPlanLoadingAtom,
    mealPlanInstructionsErrorAtom,
    mealPlanSummaryErrorAtom,
    detailedMealPlanErrorAtom,
    activeMealPlanStepAtom,
    hasSetInitialStepAtom,
    includeMacroDataAtom,
    editDetailedMealPlanAtom,
    detailedMealPlanEditModeAtom,
    isMealPlanBusyAtom,
    isMealPlanUpdatingAtom,
    mealPlanInstructionsAtom,
    mealPlanSummaryAtom,
    detailedMealPlanAtom,
    previousAppointmentIdAtom,
    refetchMealPlanInstructionsAtom,
    refetchMealPlanSummaryAtom,
    refetchDetailedMealPlanAtom,
    refetchMealPlanAtom,
    resetMealPlanAtom,
    analyticsContextAtom,
    isStandalonePageOpenAtom,
    isMealPlanSentAtom,
  };
});
